import { useState } from 'react';
import { Eye, EyeOff, RotateCcw, GripVertical } from 'lucide-react';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  fixed?: boolean;
}

interface ColumnConfigPanelProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  defaultColumns: ColumnConfig[];
}

export default function ColumnConfigPanel({ columns, onColumnsChange, defaultColumns }: ColumnConfigPanelProps) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const visibleCount = columns.filter(c => c.visible).length;

  const toggleVisibility = (id: string) => {
    onColumnsChange(columns.map(c =>
      c.id === id && !c.fixed ? { ...c, visible: !c.visible } : c
    ));
  };

  const handleReset = () => {
    onColumnsChange(defaultColumns.map(c => ({ ...c })));
  };

  const handleDragStart = (index: number) => {
    if (columns[index].fixed) return;
    setDraggingIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (columns[index].fixed) return;
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === targetIndex) return;
    if (columns[targetIndex].fixed) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggingIndex, 1);
    newColumns.splice(targetIndex, 0, removed);
    onColumnsChange(newColumns);
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="w-64 bg-[#2d3a5e] rounded-xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-bold text-white uppercase tracking-widest">Colonnes</h3>
          <p className="text-[11px] text-white/50 mt-0.5">
            {visibleCount} affichées · Glisser pour réordonner
          </p>
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-1 px-2 py-1 text-xs text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
          title="Réinitialiser"
        >
          <RotateCcw size={11} />
          Reset
        </button>
      </div>

      {/* Column list */}
      <div className="py-1 max-h-80 overflow-y-auto">
        {columns.map((col, index) => (
          <div
            key={col.id}
            draggable={!col.fixed}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 px-3 py-2 transition-colors select-none ${
              col.fixed
                ? 'opacity-40 cursor-default'
                : 'cursor-grab active:cursor-grabbing hover:bg-white/5'
            } ${
              dragOverIndex === index && draggingIndex !== index
                ? 'bg-white/10 border-t-2 border-indigo-400'
                : ''
            } ${draggingIndex === index ? 'opacity-40' : ''}`}
          >
            <GripVertical
              size={14}
              className={col.fixed ? 'text-white/20' : 'text-white/40'}
            />
            <button
              onClick={() => toggleVisibility(col.id)}
              disabled={col.fixed}
              className={`shrink-0 transition-colors ${col.fixed ? 'cursor-default' : 'hover:scale-110'}`}
            >
              {col.visible ? (
                <Eye size={15} className="text-indigo-400" />
              ) : (
                <EyeOff size={15} className="text-white/25" />
              )}
            </button>
            <span className={`text-sm leading-none ${col.visible ? 'text-white/90' : 'text-white/35'}`}>
              {col.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
